/* eslint-disable no-nested-ternary */

import React from 'react';
import {Dropdown, OverlayTrigger, Tooltip} from 'react-bootstrap';
import {
  useTable,
  useFlexLayout,
  useResizeColumns,
  useAsyncDebounce,
  useSortBy,
  usePagination,
  useFilters,
  // useExpanded,
} from 'react-table';
import {StyledLabel} from '@styled';

import {
  TableWrapper1,
  GridHeader,
  GridRow,
  SortOrderIcon,
  GridColText,
} from './styles';
import DefaultColumnFilter from './DefaultColumnFilter';
import Pagination from '../misc/Pagination';

function HeaderColumnMenu({
  columns,
  hiddenColumns,
  // getToggleHideAllColumnsProps,
  onHideColumn = () => ({}),
}) {
  const handleColumnChecked = (e, fn, column) => {
    fn(e);
    onHideColumn(column, !e.target.checked);
  };

  const filteredColumns = React.useMemo(() => {
    return columns.filter((column) => {
      return !hiddenColumns.includes(column.id || column.accessor);
    });
  }, [columns, hiddenColumns]);

  return (
    <Dropdown
      style={{position: 'absolute', right: '0px', bottom: '1px'}}
      className="d-none d-lg-block"
    >
      <Dropdown.Toggle
        className="transparent-btn"
        style={{
          fontSize: '10px',
          padding: '5px',
        }}
      >
        <i className="fa fa-ellipsis-v" />
      </Dropdown.Toggle>
      <Dropdown.Menu style={{padding: '10px'}}>
        {filteredColumns.map((column) => {
          const {onChange, ...colProps} = {
            ...column.getToggleHiddenProps(),
          };
          return column.id === 'actions' ? null : (
            <div key={column.id}>
              <label style={{fontWeight: 400, fontSize: '.8rem'}}>
                <input
                  type="checkbox"
                  {...colProps}
                  onChange={(e) =>
                    handleColumnChecked(
                      e,
                      onChange,
                      column.id || column.accessor
                    )
                  }
                />{' '}
                {column.id}
              </label>
            </div>
          );
        })}
      </Dropdown.Menu>
    </Dropdown>
  );
}

// const headerProps = (props, {column}) => getStyles(props, column.align);

// const cellProps = (props, {cell}) => getStyles(props, cell.column.align);

// const getStyles = (props, align = 'left') => [
//   props,
//   {
//     style: {
//       justifyContent: align === 'right' ? 'flex-end' : 'flex-start',
//       alignItems: 'flex-start',
//       display: 'flex',
//     },
//   },
// ];

function ReactTableBootstrap({
  columns,
  data,
  showPagination = true,
  pagination,
  onFetchData,
  filtering = false,
  extraData = {},
  getRowStyles = (row) => ({row}),
  onHideColumn = (hidden) => ({hidden}),
  noDataFound = 'No records found',
  initState = {},
  stateFilters = [],
  onStateChange = (newState, action, prevState) => ({
    newState,
    action,
    prevState,
  }),
  expandEnabled = false,
  expandedRows = [],
  expandRowId = 'id',
  expandedRowRender = (row) => row,
}) {
  const defaultColumn = React.useMemo(
    () => ({
      minWidth: 10,
      width: 50,
      // maxWidth: 400,
      Filter: DefaultColumnFilter,
    }),
    []
  );

  const hiddenColumns = React.useMemo(() => {
    return columns
      .filter((column) => column.hidden)
      .map((column) => column.accessor || column.id);
  }, [columns]);

  // Use the state and functions returned from useTable to build your UI
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    allColumns,
    getToggleHideAllColumnsProps,
    // page, // Instead of using 'rows', we'll use page,
    // which has only the rows for the active page
    // The rest of these things are super handy, too ;)
    // canPreviousPage,
    // canNextPage,
    pageOptions,
    pageCount,
    gotoPage,
    // nextPage,
    // previousPage,
    setPageSize,
    state: {pageIndex, pageSize, sortBy, filters},
    setAllFilters,
  } = useTable(
    {
      columns,
      data,
      defaultColumn,
      initialState: {
        pageSize: pagination.pageSize || 10,
        hiddenColumns,
        ...initState,
        pageIndex: Math.max(pagination.page - 1, 0),
      },
      autoResetHiddenColumns: false,
      manualPagination: true,
      pageCount: Math.max(
        Math.ceil(pagination.totalCount / pagination.pageSize),
        1
      ),
      manualSortBy: true,
      disableMultiSort: false,
      manualFilters: true,
      disableFilters: !filtering,
      extraData,
      stateReducer: (newState, action, prevState) => {
        // console.log("action", action, JSON.stringify(newState));
        onStateChange(newState, action, prevState);
      },
    },
    useFlexLayout,
    useResizeColumns,
    useFilters,
    useSortBy,
    // expandedRows ? useExpanded : () => {},
    usePagination
  );

  // Debounce our onFetchData call for 100ms
  const onFetchDataDebounced = useAsyncDebounce(onFetchData, 100);

  React.useEffect(() => {
    // console.log(
    //   "pageIndex",
    //   "pageSize",
    //   pageIndex,
    //   pageSize,
    //   sortBy,
    //   filters,
    //   stateFilters
    // );
    // if (
    //   pagination.page !== pageIndex + 1 ||
    //   pagination.pageSize !== pageSize ||
    //   pagination.sort !== genSortField(sortBy) ||
    //   JSON.stringify(stateFilters) !== JSON.stringify(filters)
    // )
    onFetchDataDebounced({
      page: pageIndex + 1,
      pageSize,
      sortOn: sortBy.length ? sortBy[0].id : undefined,
      sortDir: sortBy.length ? (sortBy[0].desc ? 'DESC' : 'ASC') : undefined,
      filters,
    });
  }, [pageIndex, pageSize, sortBy, filters]);

  React.useEffect(() => {
    // console.log("state filters changed", stateFilters, filters);
    if (filters.length !== stateFilters.length) {
      setAllFilters(stateFilters);
    }
  }, [stateFilters]);

  React.useEffect(() => {
    if (pageIndex + 1 !== pagination.page) {
      gotoPage(Math.max(pagination.page - 1, 0));
    }
  }, [pagination.page]);

  const isExpandedRow = React.useCallback(
    (row) => {
      if (expandEnabled) {
        //  find row expanded if any
        const found = expandedRows.find(
          (eRow) => eRow === row.original[expandRowId]
        );
        if (found) {
          return true;
        }
      }
      return false;
    },
    [expandEnabled, expandRowId, expandedRows]
  );

  // Render the UI for your table
  return (
    <TableWrapper1>
      <div {...getTableProps()} className="table">
        <div>
          {headerGroups.map((headerGroup) => (
            <div {...headerGroup.getHeaderGroupProps()} className="tr">
              {headerGroup.headers.map((column) => (
                <div
                  // className="th"
                  {...column.getHeaderProps([
                    {
                      className: `th ${column.className || ''}`,
                      style: column.style,
                    },
                  ])}
                >
                  <GridHeader {...column.getSortByToggleProps()}>
                    <div
                      className="cell-content content-overflow"
                      style={{
                        width:
                          column.id !== 'actions'
                            ? 'calc(100% - 40px)'
                            : '100%',
                        display: 'inline-block',
                        verticalAlign: '-0.1rem',
                      }}
                    >
                      {column.render('Header')}
                    </div>
                    <SortOrderIcon className="sort-directions-icon d-none d-lg-inline">
                      {column.isSorted ? (
                        column.isSortedDesc ? (
                          <i className="fa fa-caret-down" />
                        ) : (
                          <i className="fa fa-caret-up" />
                        )
                      ) : (
                        ''
                      )}
                    </SortOrderIcon>
                  </GridHeader>
                  {column.canFilter ? (
                    <Dropdown
                      // isOpen={dropdownOpen}
                      // toggle={toggle}
                      style={{
                        position: 'absolute',
                        right: '16px',
                        top: '0px',
                      }}
                      className="d-none d-lg-block"
                    >
                      <Dropdown.Toggle
                        className={`transparent-btn ${
                          column.filterValue
                            ? 'filter-btn-selected'
                            : 'filter-btn-normal'
                        }`}
                        style={{
                          fontSize: '10px',
                          padding: '5px 2px',
                        }}
                        id={`filter-menu-toggle-${column.id}`}
                      >
                        <i className="fa fa-filter" />
                      </Dropdown.Toggle>
                      <Dropdown.Menu style={{padding: '10px'}}>
                        <div>{column.render('Filter')}</div>
                      </Dropdown.Menu>
                    </Dropdown>
                  ) : null}
                  {/* {column.id !== "actions" ? ( */}
                  <HeaderColumnMenu
                    columns={allColumns}
                    hiddenColumns={hiddenColumns}
                    getToggleHideAllColumnsProps={getToggleHideAllColumnsProps}
                    onHideColumn={onHideColumn}
                  />
                  {/* ) : null} */}
                  {/* Use column.getResizerProps to hook up the events correctly */}
                  <div
                    {...column.getResizerProps()}
                    className={`d-none d-lg-block resizer ${
                      column.isResizing ? 'isResizing' : ''
                    }`}
                  />
                </div>
              ))}
            </div>
          ))}
        </div>

        <div {...getTableBodyProps()} className="tbody">
          {rows.map((row) => {
            prepareRow(row);
            return (
              <>
                <GridRow
                  className={isExpandedRow(row) ? 'tr' : 'tr mb-1'}
                  {...row.getRowProps(getRowStyles(row))}
                >
                  {row.cells.map((cell) => {
                    return (
                      <GridColText
                        {...cell.getCellProps([
                          {
                            className: `td content-overflow ${
                              cell.column.className || ''
                            }`,
                            style: cell.column.style,
                          },
                        ])}
                        // className={`td content-overflow`}
                      >
                        {cell.column.tooltip ? (
                          <OverlayTrigger
                            overlay={
                              <Tooltip
                                id={`tooltip-column-${
                                  cell.column.id || cell.column.accessor
                                }`}
                              >
                                {cell.render('Cell')}
                              </Tooltip>
                            }
                            placement="bottom"
                          >
                            <div
                              className="content-overflow d-inline-block"
                              style={{width: '100%'}}
                            >
                              {cell.render('Cell')}
                            </div>
                          </OverlayTrigger>
                        ) : (
                          cell.render('Cell')
                        )}
                      </GridColText>
                    );
                  })}
                </GridRow>
                {expandEnabled && (
                  <GridRow
                    className="tr mb-1"
                    style={{
                      backgroundColor: '#f1f1f1',
                      border: '2px solid #469540',
                      display: isExpandedRow(row) ? undefined : 'none',
                    }}
                    id={`expand-row-${row.original[expandRowId]}`}
                  >
                    {expandedRowRender(row)}
                  </GridRow>
                )}
              </>
            );
          })}
        </div>
        {rows.length === 0 ? <StyledLabel>{noDataFound}</StyledLabel> : null}
      </div>
      {showPagination ? (
        <Pagination
          page={pageIndex + 1}
          pageCount={pageCount}
          pageOptions={pageOptions}
          gotoPage={gotoPage}
          pageSize={pageSize}
          setPageSize={setPageSize}
        />
      ) : null}
    </TableWrapper1>
  );
}

export default ReactTableBootstrap;
